/**
 * Multihub Lead Capture Block
 *
 * Global injection block that loads the Multihub lead capture script.
 * Resolves the correct LCID from the property's lead_collectors array
 * based on the configured lead_source (PROPERTY_WEBSITE or LANDING_PAGE).
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import { useProperty } from '@vibesites/runtime/react';

const SCRIPT_ID = 'mh-lead-capture-script';

function getLeadsDomain(): string {
  const apiUrl = (import.meta as any).env?.MULTIHUB_API_URL || '';
  if (apiUrl.includes('localhost') || apiUrl.includes('data-testing'))
    return 'https://testing.leads.multihub.io';
  if (apiUrl.includes('data-dev'))
    return 'https://dev.leads.multihub.io';
  return 'https://leads.multihub.io';
}

function MultihubLeadCaptureComponent(_props: BlockProps) {
  const leadSource = useBlockToken('lead_source', { defaultValue: 'PROPERTY_WEBSITE' });
  const property = useProperty();
  const mountedRef = useRef(true);

  const propertyId = property?.multihub_property_id;
  const leadCollectors = property?.lead_collectors;

  const collector = leadCollectors?.find(
    (lc) => lc.lead_source === leadSource && !lc.deleted_at
  );
  const lcid = collector?.lead_collector_id;

  useEffect(() => {
    mountedRef.current = true;

    if (!lcid || !propertyId) {
      if (!lcid) console.log(`[MultihubLeadCapture] No lead collector found for source "${leadSource}"`);
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();

    const domain = getLeadsDomain();

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `${domain}/leadcapture.js`;
    script.defer = true;
    script.dataset.beaconEndpoint = `${domain}/beacon`;
    script.dataset.mhEndpoint = `${domain}/form`;
    script.dataset.lcid = lcid;
    script.dataset.mhPropertyid = propertyId;
    document.head.appendChild(script);

    return () => {
      mountedRef.current = false;
      const el = document.getElementById(SCRIPT_ID);
      if (el) el.remove();
    };
  }, [lcid, propertyId, leadSource]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'MultihubLeadCapture',
  displayName: 'Multihub Lead Capture',
  tokens: {
    lead_source: {
      label: 'Source Type',
      type: 'text',
      defaultValue: 'PROPERTY_WEBSITE',
      description: 'Lead source type to match. Valid values: PROPERTY_WEBSITE, LANDING_PAGE.',
    },
  },
};

export const multihubLeadCaptureBlock: BlockDefinition = {
  id: 'multihub-lead-capture',
  name: 'Multihub Lead Capture',
  description: 'Injects the Multihub lead capture script, automatically resolved from the property\'s lead collectors.',
  category: 'Integrations',
  placement: 'injection',
  component: MultihubLeadCaptureComponent,
  contentSchema,
  tags: ['lead', 'capture', 'analytics', 'tracking', 'multihub'],
};
